import React, { useEffect, useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import PageShell, { SectionTitle } from './PageShell';
import { api } from '../lib/api';
import useMetaTags from '../lib/useMetaTags';
import { ArrowLeft, Clock, Loader2, BookOpen, ChevronRight, Sparkles, ArrowRight } from 'lucide-react';
import { DemoEmbed, DEMOS } from '../components/demos';

const TYPE_LABEL = {
  enquete: 'Édito Enquête',
  dossier_etg: 'Dossier ETG',
  guide: 'Guide pratique',
  news: 'Actu',
};

// Une du numéro en cours (N°34 · Octobre 2026)
const UNE_SLUG = 'quadricycle-lourd-l7e-permis-b1-guide-complet';

// Slugs du numéro précédent (N°33) gardés en sommaire secondaire
const ISSUE_33_SLUGS = [
  'marquages-au-sol-le-langage-des-lignes',
  'priorites-les-5-regles-que-tout-le-monde-oublie',
  'feux-tricolores-vert-jaune-orange-clignotant-decrypte',
  'permis-des-17-ans-bilan-2-ans-apres',
];

function UneOctobre({ article }) {
  if (!article) return null;
  const UNE_VIDEO = '/assets/videos/une-l7e.mp4';
  return (
    <section className="max-w-7xl mx-auto px-4 lg:px-6 mt-8 sm:mt-12" data-testid="une-octobre-2026">
      <div className="rounded-2xl sm:rounded-3xl border border-red-700/40 bg-gradient-to-br from-[#1a0a0d] via-[#0e0709] to-[#0d0d0e] overflow-hidden">
        <div className="grid lg:grid-cols-[1.15fr_1fr]">
          {/* Vidéo de une — trafic urbain en mouvement */}
          <Link to={`/magazine/${article.slug}`} className="group relative block aspect-[16/10] lg:aspect-auto lg:min-h-[380px] overflow-hidden" data-testid="une-octobre-image">
            <video
              data-testid="une-octobre-video"
              autoPlay
              loop
              muted
              playsInline
              preload="metadata"
              poster={article.cover_image}
              className="absolute inset-0 w-full h-full object-cover"
              aria-hidden="true"
            >
              <source src={UNE_VIDEO} type="video/mp4" />
            </video>
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent lg:bg-gradient-to-r" />
            <div className="absolute top-4 left-4 flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-600 text-white text-[10px] sm:text-[11px] font-bold tracking-widest font-mono-spec">
                <Sparkles size={11} /> À LA UNE
              </span>
              <span className="px-2 py-0.5 rounded bg-black/60 backdrop-blur border border-white/20 text-white text-[10px] font-bold tracking-wider">
                {TYPE_LABEL[article.type] || article.type}
              </span>
            </div>
          </Link>
          {/* Texte de une */}
          <div className="p-5 sm:p-8 lg:p-10 flex flex-col justify-center">
            <div className="text-red-400 font-mono-spec text-[11px] sm:text-[12px] tracking-widest uppercase mb-3">
              N°34 · Octobre 2026 {article.theme ? `· ${article.theme}` : ''}
            </div>
            <Link to={`/magazine/${article.slug}`} className="group">
              <h2 className="font-serif-editorial text-white text-[26px] sm:text-[34px] lg:text-[40px] leading-[1.05] group-hover:text-red-100 transition-colors">
                {article.title}
              </h2>
            </Link>
            {article.subtitle && (
              <p className="text-white/70 text-[13.5px] sm:text-[15px] leading-relaxed mt-4 max-w-xl">{article.subtitle}</p>
            )}
            <div className="mt-5 flex flex-wrap items-center gap-2 sm:gap-3 text-[11px] sm:text-[12px] font-mono-spec text-white/40 uppercase tracking-widest">
              <span className="inline-flex items-center gap-1"><Clock size={11} /> {article.read_time_min} min de lecture</span>
              <span className="text-white/20">·</span>
              <span>Sources : Légifrance & Sécurité Routière</span>
            </div>
            <Link to={`/magazine/${article.slug}`} data-testid="une-octobre-cta"
              className="mt-6 inline-flex items-center gap-2 h-11 px-6 rounded-full bg-red-600 hover:bg-red-500 text-white text-[13.5px] font-bold w-fit transition">
              Lire le dossier <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function IssueCoverN33({ articles }) {
  if (!articles || articles.length === 0) return null;
  // Order articles to match ISSUE_33_SLUGS order
  const ordered = ISSUE_33_SLUGS
    .map((s) => articles.find((a) => a.slug === s))
    .filter(Boolean);
  if (ordered.length === 0) return null;

  return (
    <section
      className="max-w-7xl mx-auto px-4 lg:px-6 mt-8 sm:mt-12"
      data-testid="issue-33-cover"
    >
      <div className="rounded-2xl sm:rounded-3xl border border-red-700/40 bg-gradient-to-br from-[#1a0a0d] via-[#0e0709] to-[#0d0d0e] overflow-hidden">
        {/* Header éditorial */}
        <div className="p-5 sm:p-8 lg:p-10 border-b border-red-700/20">
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/10 border border-white/15 text-white/80 text-[10px] sm:text-[11px] font-bold tracking-widest font-mono-spec">
              <BookOpen size={11} /> NUMÉRO PRÉCÉDENT
            </span>
            <span className="text-red-400 font-mono-spec text-[11px] sm:text-[12px] tracking-widest uppercase">
              N°33 · Juillet 2026
            </span>
          </div>

          <h2 className="font-serif-editorial text-white text-[26px] sm:text-[38px] lg:text-[48px] leading-[1.05] max-w-3xl">
            Le dossier « fondamentaux »
          </h2>

          <p className="text-white/70 text-[13.5px] sm:text-[15.5px] leading-relaxed mt-4 sm:mt-5 max-w-3xl">
            Marquages au sol, priorités, feux tricolores : trois piliers du Code de la route
            que <em>chaque</em> conducteur croit maîtriser — et que 70 % appliquent mal. Ce
            numéro reprend, calmement, ce langage silencieux qui fait la différence entre
            un permis obtenu et un permis vécu. Bonus : premier bilan complet du{' '}
            <strong className="text-white">permis dès 17 ans</strong>, deux ans après.
          </p>

          <div className="mt-5 sm:mt-6 flex flex-wrap items-center gap-2 sm:gap-3 text-[11px] sm:text-[12px] font-mono-spec text-white/40 uppercase tracking-widest">
            <span>{ordered.length} articles</span>
            <span className="text-white/20">·</span>
            <span>{ordered.reduce((acc, a) => acc + (a.read_time_min || 0), 0)} min de lecture</span>
            <span className="text-white/20">·</span>
            <span>Rédigé par un ECSR diplômé d&apos;État</span>
          </div>
        </div>

        {/* Grille sommaire */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-red-700/20">
          {ordered.map((a, i) => (
            <Link
              key={a.id}
              to={`/magazine/${a.slug}`}
              data-testid={`issue-33-article-${i}`}
              className="group relative bg-[#0d0d0e] hover:bg-[#151112] transition p-4 sm:p-5 lg:p-6 flex flex-col"
            >
              <div className="aspect-[16/10] rounded-lg overflow-hidden mb-3 sm:mb-4 relative bg-[#151112]">
                {a.cover_image && (
                  <img
                    loading="lazy"
                    src={a.cover_image}
                    alt={a.title}
                    className="absolute inset-0 w-full h-full object-cover img-zoom ken-burns"
                  />
                )}
                <div className="absolute top-2 left-2 px-1.5 py-0.5 rounded bg-red-600 text-white text-[9px] sm:text-[10px] font-bold tracking-wider">
                  {TYPE_LABEL[a.type] || a.type}
                </div>
                <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-black/60 backdrop-blur border border-white/20 flex items-center justify-center text-white text-[10px] font-bold font-mono-spec">
                  {i + 1}
                </div>
              </div>

              <div className="text-[10px] sm:text-[11px] text-red-400/80 font-mono-spec mb-1 tracking-wider uppercase truncate">
                {a.theme}
              </div>
              <h3 className="font-serif-editorial text-white text-[15px] sm:text-[18px] lg:text-[19px] leading-tight line-clamp-3 group-hover:text-red-100 transition-colors">
                {a.title}
              </h3>
              <div className="mt-auto pt-3 flex items-center justify-between text-[10.5px] sm:text-[11.5px] font-mono-spec text-white/40">
                <span className="inline-flex items-center gap-1">
                  <Clock size={10} /> {a.read_time_min} min
                </span>
                <span className="inline-flex items-center gap-1 text-red-400 group-hover:gap-2 transition-all">
                  Lire <ArrowRight size={11} />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export function MagazineIndexPage() {
  const [type, setType] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allArticles, setAllArticles] = useState([]);

  // Load all articles once for the N°33 cover
  useEffect(() => {
    api.listArticles({}).then((d) => setAllArticles(d));
  }, []);

  useEffect(() => {
    setLoading(true);
    api.listArticles(type ? { type } : {}).then((d) => { setItems(d); setLoading(false); });
  }, [type]);

  const issue33Articles = useMemo(
    () => (allArticles || []).filter((a) => ISSUE_33_SLUGS.includes(a.slug)),
    [allArticles]
  );
  const uneArticle = useMemo(
    () => (allArticles || []).find((a) => a.slug === UNE_SLUG),
    [allArticles]
  );

  const filters = [
    { v: null, l: 'Tout' },
    { v: 'enquete', l: 'Enquêtes' },
    { v: 'dossier_etg', l: 'Dossiers ETG' },
    { v: 'guide', l: 'Guides' },
    { v: 'news', l: 'Actu' },
  ];

  return (
    <PageShell>
      {/* Une du numéro en cours — N°34 Octobre 2026 */}
      <UneOctobre article={uneArticle} />

      {/* Sommaire du numéro précédent */}
      <IssueCoverN33 articles={issue33Articles} />

      <section className="max-w-7xl mx-auto px-4 lg:px-6 mt-12">
        <SectionTitle kicker="Magazine · Code Voiture" title="Toutes nos publications" sub="Enquêtes terrain, dossiers ETG pédagogiques, guides pratiques et actu sécurité routière." />
        <div className="flex flex-wrap gap-2 mb-8">
          {filters.map((f) => (
            <button key={f.l} onClick={() => setType(f.v)} className={`px-4 h-9 rounded-full text-[13px] font-semibold border transition ${type === f.v ? 'border-red-500 bg-red-600 text-white' : 'border-white/15 bg-white/5 text-white/70 hover:border-white/30'}`}>
              {f.l}
            </button>
          ))}
        </div>
        {loading ? (
          <div className="text-white/50 inline-flex items-center gap-2 py-10"><Loader2 size={16} className="animate-spin" /> Chargement…</div>
        ) : items.length === 0 ? (
          <div className="text-white/50 py-10">Aucune publication pour ce filtre pour l’instant.</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {items.map((a) => (
              <Link key={a.id} to={`/magazine/${a.slug}`} className="group rounded-xl sm:rounded-2xl border border-white/10 bg-[#111113] overflow-hidden hover:border-red-700/40 transition">
                <div className="aspect-[16/10] sm:aspect-[16/10] bg-[#0d0d0e] relative overflow-hidden">
                  {a.cover_image ? <img loading="lazy" src={a.cover_image} alt={a.title} className="absolute inset-0 w-full h-full object-cover img-zoom" /> : <div className="absolute inset-0 flex items-center justify-center text-white/20"><BookOpen size={28} /></div>}
                  <div className="absolute top-2 left-2 sm:top-3 sm:left-3 px-1.5 sm:px-2 py-0.5 rounded bg-red-600 text-white text-[9px] sm:text-[10px] font-bold tracking-wider">{TYPE_LABEL[a.type] || a.type}</div>
                </div>
                <div className="p-3 sm:p-5">
                  {a.issue_number && <div className="text-[10px] sm:text-[11px] text-red-400 font-mono-spec mb-0.5 sm:mb-1 truncate">{a.issue_number}{a.theme ? ` · ${a.theme}` : ''}</div>}
                  <h3 className="font-serif-editorial text-white text-[14px] sm:text-[20px] leading-tight line-clamp-3">{a.title}</h3>
                  {a.subtitle && <p className="hidden sm:block text-white/55 text-[13px] mt-2 line-clamp-3">{a.subtitle}</p>}
                  <div className="mt-2 sm:mt-4 flex items-center gap-1.5 text-[10.5px] sm:text-[11.5px] text-white/40 font-mono-spec"><Clock size={10} /> {a.read_time_min} min</div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </PageShell>
  );
}

function renderMarkdown(md) {
  // Tiny markdown: paragraphs, ## headings, **bold**, _italic_, lists, blockquotes
  // Plus [DEMO:slug] markers → React component embed
  if (!md) return null;
  const lines = md.split('\n');
  const blocks = [];
  let buf = [];
  const flushPara = () => { if (buf.length) { blocks.push({ type: 'p', text: buf.join(' ') }); buf = []; } };
  for (const raw of lines) {
    const l = raw.trim();
    if (!l) { flushPara(); continue; }
    const demoMatch = l.match(/^\[DEMO:([a-z-]+)\]$/);
    if (demoMatch && DEMOS[demoMatch[1]]) { flushPara(); blocks.push({ type: 'demo', slug: demoMatch[1] }); continue; }
    const imgMatch = l.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
    if (imgMatch) { flushPara(); blocks.push({ type: 'img', alt: imgMatch[1], src: imgMatch[2] }); continue; }
    const callOpen = l.match(/^:::(\w+)\s*(.*)$/);
    if (callOpen) { flushPara(); blocks.push({ type: 'callout-open', variant: callOpen[1], title: callOpen[2] }); continue; }
    if (l === ':::') { flushPara(); blocks.push({ type: 'callout-close' }); continue; }
    if (l.startsWith('|')) { flushPara(); blocks.push({ type: 'trow', cells: l.split('|').slice(1, -1).map((c) => c.trim()) }); continue; }
    if (l.startsWith('## ')) { flushPara(); blocks.push({ type: 'h2', text: l.slice(3) }); continue; }
    if (l.startsWith('# ')) { flushPara(); blocks.push({ type: 'h1', text: l.slice(2) }); continue; }
    if (l.startsWith('> ')) { flushPara(); blocks.push({ type: 'quote', text: l.slice(2) }); continue; }
    if (l.startsWith('- ')) { flushPara(); blocks.push({ type: 'li', text: l.slice(2) }); continue; }
    buf.push(l);
  }
  flushPara();
  // Compact lists + tables + callouts
  const out = [];
  for (let i = 0; i < blocks.length; i++) {
    if (blocks[i].type === 'li') {
      const items = [];
      while (i < blocks.length && blocks[i].type === 'li') { items.push(blocks[i].text); i++; }
      i--;
      out.push({ type: 'ul', items });
    } else if (blocks[i].type === 'trow') {
      const rows = [];
      while (i < blocks.length && blocks[i].type === 'trow') { rows.push(blocks[i].cells); i++; }
      i--;
      const body = rows.filter((r) => !r.every((c) => /^:?-{2,}:?$/.test(c)));
      out.push({ type: 'table', header: body[0], rows: body.slice(1) });
    } else if (blocks[i].type === 'callout-open') {
      const { variant, title } = blocks[i];
      const children = [];
      i++;
      while (i < blocks.length && blocks[i].type !== 'callout-close') { children.push(blocks[i]); i++; }
      out.push({ type: 'callout', variant, title, children });
    } else {
      out.push(blocks[i]);
    }
  }
  const inline = (t) => {
    // links, bold and italic
    let parts = [t];
    parts = parts.flatMap((p) => typeof p === 'string' ? p.split(/(\[[^\]]+\]\([^)]+\))/g) : [p]).map((s) => {
      if (typeof s === 'string') {
        const m = s.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
        if (m) return <a key={Math.random()} href={m[2]} target="_blank" rel="noopener noreferrer" className="text-red-400 hover:text-red-300 underline underline-offset-2">{m[1]}</a>;
      }
      return s;
    });
    parts = parts.flatMap((p) => typeof p === 'string' ? p.split(/(\*\*[^*]+\*\*)/g) : [p]).map((s) => typeof s === 'string' && /^\*\*[^*]+\*\*$/.test(s) ? <strong key={Math.random()} className="text-white">{s.slice(2,-2)}</strong> : s);
    parts = parts.flatMap((p) => typeof p === 'string' ? p.split(/(_[^_]+_)/g) : [p]).map((s) => typeof s === 'string' && /^_[^_]+_$/.test(s) ? <em key={Math.random()} className="italic text-white/85">{s.slice(1,-1)}</em> : s);
    return parts;
  };
  return out.map((b, i) => {
    if (b.type === 'demo') return <DemoEmbed key={i} slug={b.slug} />;
    if (b.type === 'callout') return <Callout key={i} variant={b.variant} title={b.title} blocks={b.children} inline={inline} />;
    if (b.type === 'table') return (
      <div key={i} className="my-8 overflow-x-auto rounded-xl border border-white/10">
        <table className="w-full text-left border-collapse min-w-[560px]" data-testid="article-table">
          <thead>
            <tr className="bg-white/[0.04] border-b border-white/10">
              {b.header.map((h, j) => (
                <th key={j} className={`py-3 px-4 text-[11px] uppercase tracking-widest font-mono-spec font-bold ${j === 0 ? 'text-white/45' : 'text-red-300'}`}>{inline(h)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {b.rows.map((r, ri) => (
              <tr key={ri} className="border-b border-white/5 last:border-0">
                {r.map((c, ci) => (
                  <td key={ci} className={`py-3 px-4 text-[13px] leading-snug ${ci === 0 ? 'text-white/55 font-semibold' : 'text-white/85'}`}>{inline(c)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
    if (b.type === 'img') return (
      <figure key={i} className="my-8">
        <div className="rounded-xl sm:rounded-2xl overflow-hidden border border-white/10">
          <img loading="lazy" src={b.src} alt={b.alt} className="w-full h-auto" />
        </div>
        {b.alt && <figcaption className="mt-2 text-white/40 text-[12px] font-mono-spec text-center">{b.alt}</figcaption>}
      </figure>
    );
    if (b.type === 'h1') return <h1 key={i} className="font-serif-editorial text-white text-[36px] leading-tight mt-12 mb-5">{b.text}</h1>;
    if (b.type === 'h2') return <h2 key={i} className="font-serif-editorial text-white text-[26px] leading-tight mt-10 mb-4">{b.text}</h2>;
    if (b.type === 'quote') return <blockquote key={i} className="border-l-4 border-red-500 pl-5 my-6 text-white/85 italic text-[16px]">{inline(b.text)}</blockquote>;
    if (b.type === 'ul') return <ul key={i} className="my-5 space-y-2 list-disc list-inside text-white/80 text-[15px]">{b.items.map((it, j) => <li key={j}>{inline(it)}</li>)}</ul>;
    return <p key={i} className="text-white/80 text-[15.5px] leading-[1.75] my-4">{inline(b.text)}</p>;
  });
}

function Callout({ variant, title, blocks, inline }) {
  const [revealed, setRevealed] = useState(false);
  const styles = {
    info: { border: 'border-blue-900/50', bg: 'bg-gradient-to-br from-[#0e1526] to-[#0d0d0e]', accent: 'text-blue-300', label: title || 'Le saviez-vous ?' },
    defi: { border: 'border-amber-700/40', bg: 'bg-gradient-to-br from-[#1a1206] to-[#0d0d0e]', accent: 'text-amber-300', label: title || 'Défi du jour' },
    manoeuvre: { border: 'border-red-900/40', bg: 'bg-[#120a0b]', accent: 'text-red-300', label: title || 'Manœuvre' },
  };
  const s = styles[variant] || styles.info;
  const visible = [];
  let hidden = null;
  blocks.forEach((b) => {
    if (b.type === 'p' && b.text.startsWith('REPONSE:')) hidden = b.text.slice(8).trim();
    else visible.push(b);
  });
  return (
    <div className={`my-8 rounded-2xl border ${s.border} ${s.bg} p-5 sm:p-6`} data-testid={`callout-${variant}`}>
      <div className={`${s.accent} text-[10.5px] uppercase tracking-[0.2em] font-mono-spec font-bold mb-2.5`}>{s.label}</div>
      {visible.map((b, i) => {
        if (b.type === 'ul' || b.type === 'li') return null;
        return <p key={i} className="text-white/80 text-[14px] leading-relaxed my-1.5">{inline(b.text)}</p>;
      })}
      {blocks.filter((b) => b.type === 'li').length > 0 && (
        <ul className="mt-2 space-y-1.5 list-disc list-inside text-white/75 text-[13.5px]">
          {blocks.filter((b) => b.type === 'li').map((b, i) => <li key={i}>{inline(b.text)}</li>)}
        </ul>
      )}
      {hidden && (
        revealed ? (
          <div className="mt-3 rounded-xl bg-emerald-950/20 border border-emerald-700/30 p-3.5 text-emerald-200 text-[13.5px] leading-relaxed" data-testid="defi-reponse">{inline(hidden)}</div>
        ) : (
          <button type="button" onClick={() => setRevealed(true)} data-testid="defi-reveal-btn"
            className="mt-3 h-10 px-5 rounded-full bg-amber-600 hover:bg-amber-500 text-black text-[13px] font-bold transition">
            Voir la réponse
          </button>
        )
      )}
    </div>
  );
}

export function ArticlePage() {
  const { slug } = useParams();
  const [a, setA] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  useEffect(() => {
    setLoading(true);
    api.getArticle(slug).then((d) => { setA(d); setLoading(false); }).catch((e) => { setErr(e?.response?.data?.detail || 'Article introuvable'); setLoading(false); });
  }, [slug]);

  // SEO / OG dynamique par article
  const metaOpts = useMemo(() => {
    if (!a) return null;
    const desc = (a.subtitle || a.excerpt || (a.body_markdown || '').replace(/[#*_>`\-]/g, '').trim().slice(0, 200)).trim();
    return {
      title: `${a.title} — Code Voiture`,
      description: desc || 'Article Magazine — Code Voiture.',
      ogImage: a.cover_image || undefined,
      ogUrl: `https://codevoiture.fr/magazine/${a.slug || slug}`,
      ogType: 'article',
    };
  }, [a, slug]);
  useMetaTags(metaOpts);

  if (loading) return <PageShell><div className="max-w-3xl mx-auto p-12 text-white/50 inline-flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Chargement…</div></PageShell>;
  if (err || !a) return <PageShell><div className="max-w-3xl mx-auto p-12 text-white/50">{err}</div></PageShell>;

  return (
    <PageShell>
      <article className="max-w-3xl mx-auto px-4 lg:px-6 pt-10 pb-20">
        <Link to="/magazine" className="inline-flex items-center gap-1.5 text-white/50 hover:text-white text-[13px] mb-8"><ArrowLeft size={14} /> Tout le magazine</Link>
        <div className="flex items-center gap-3 mb-4">
          <span className="px-2 py-0.5 rounded-md bg-red-600 text-white text-[10.5px] font-bold tracking-wider">{TYPE_LABEL[a.type] || a.type}</span>
          {a.issue_number && <span className="text-red-400 text-[12px] font-mono-spec">{a.issue_number}</span>}
          {a.theme && <span className="text-white/40 text-[12px]">· {a.theme}</span>}
        </div>
        <h1 className="font-serif-editorial text-white text-[44px] lg:text-[56px] leading-[1.02] tracking-tight">{a.title}</h1>
        {a.subtitle && <p className="mt-5 text-white/65 text-[18px] leading-relaxed">{a.subtitle}</p>}
        <div className="mt-6 flex items-center gap-3 text-white/40 text-[12.5px] font-mono-spec border-y border-white/10 py-4">
          <span>Par {a.author_name}</span>
          <span>·</span>
          <Clock size={12} />
          <span>{a.read_time_min} min</span>
          <span>·</span>
          <span>{new Date(a.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
        </div>
        {a.cover_image && (
          <div className="mt-6 sm:mt-8 rounded-xl sm:rounded-2xl w-full aspect-[16/9] overflow-hidden border border-white/10">
            <img loading="lazy" src={a.cover_image} alt={a.title} className="w-full h-full object-cover ken-burns" />
          </div>
        )}
        <div className="mt-10">{renderMarkdown(a.body_markdown)}</div>
        <div className="mt-16 pt-8 border-t border-white/5">
          <Link to="/magazine" className="inline-flex items-center gap-1.5 text-red-400 hover:text-red-300 text-[14px] font-semibold">Voir plus de publications <ChevronRight size={14} /></Link>
        </div>
      </article>
    </PageShell>
  );
}
